import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { admin } from '@/lib/api';
import { buildProductPath } from '@/lib/product-url';
import { resolveProductSeoTitle, resolveProductSeoDescription } from '@/lib/product-seo';
import { getPublicSiteUrl } from '@/lib/seo';
import { toast } from 'sonner';
import { Loader2, Save, X, Eye, Pencil, Package, User, Tag, DollarSign, Box, Globe, Star, Calendar, ExternalLink, Search } from 'lucide-react';
import ProductDetailEditForm, { initProductEditForm, buildProductUpdatePayload } from '@/components/products/ProductDetailEditForm';

const ProductDetailModal = ({ productId, open, onClose, mode = 'view', hasPermission, onUpdated }) => {
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editMode, setEditMode] = useState(mode === 'edit');
  const [form, setForm] = useState({});
  const [categoryOptions, setCategoryOptions] = useState([]);

  useEffect(() => {
    let cancelled = false;
    admin
      .categories()
      .then((res) => {
        if (!cancelled) setCategoryOptions(Array.isArray(res.data) ? res.data : []);
      })
      .catch(() => {
        if (!cancelled) setCategoryOptions([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (open && productId) {
      setEditMode(mode === 'edit');
      fetchProduct();
    }
  }, [open, productId, mode]);

  const fetchProduct = async () => {
    try {
      setLoading(true);
      const res = await admin.getProduct(productId);
      setProduct(res.data);
      setForm(initProductEditForm(res.data));
    } catch {
      toast.error('Urun detayi yuklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const updateData = buildProductUpdatePayload(form, product);

      if (Object.keys(updateData).length === 0) {
        toast.info('Degisiklik yapilmadi');
        return;
      }

      await admin.updateProduct(productId, updateData);
      toast.success('Urun guncellendi');
      setEditMode(false);
      fetchProduct();
      onUpdated?.();
    } catch {
      toast.error('Urun guncellenemedi');
    } finally {
      setSaving(false);
    }
  };

  const formatShippingTry = (v) => {
    if (v === null || v === undefined || v === '') return null;
    const n = Number(v);
    if (Number.isNaN(n)) return null;
    return `${n.toFixed(2)} TRY${n === 0 ? ' (ucretsiz)' : ''}`;
  };

  const InfoRow = ({ label, value, icon: Icon }) => (
    <div className="flex items-start gap-2 py-1.5">
      {Icon && <Icon className="w-3.5 h-3.5 text-muted-foreground mt-0.5 shrink-0" />}
      <span className="text-xs text-muted-foreground w-36 shrink-0">{label}</span>
      <span className="text-sm break-all">{value || '-'}</span>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="product-detail-modal">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          </div>
        ) : product ? (
          <>
            <DialogHeader>
              <div className="flex items-center justify-between pr-6">
                <DialogTitle className="text-base flex items-center gap-2">
                  {editMode ? <Pencil className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  {editMode ? 'Urun Duzenle' : 'Urun Detayi'}
                </DialogTitle>
                <div className="flex items-center gap-1.5">
                  {!editMode && hasPermission?.('products.edit') && (
                    <Button size="sm" variant="outline" onClick={() => setEditMode(true)} data-testid="product-edit-btn">
                      <Pencil className="w-3.5 h-3.5 mr-1" /> Duzenle
                    </Button>
                  )}
                  {editMode && (
                    <>
                      <Button size="sm" variant="ghost" onClick={() => setEditMode(false)}>
                        <X className="w-3.5 h-3.5 mr-1" /> Iptal
                      </Button>
                      <Button size="sm" onClick={handleSave} disabled={saving} data-testid="product-save-btn">
                        {saving ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1" />}
                        Kaydet
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </DialogHeader>

            {/* Product Images */}
            {!editMode && product.images?.length > 0 && (
              <div className="flex gap-2 overflow-x-auto pb-2">
                {product.images.slice(0, 6).map((img, i) => (
                  <img key={i} src={img?.startsWith('/uploads/') ? img.replace('/uploads/', '/api/uploads/') : img} alt="" className="w-20 h-20 rounded-lg object-cover border shrink-0" />
                ))}
                {product.images.length > 6 && (
                  <div className="w-20 h-20 rounded-lg border flex items-center justify-center text-xs text-muted-foreground shrink-0">
                    +{product.images.length - 6}
                  </div>
                )}
              </div>
            )}

            {editMode ? (
              <ProductDetailEditForm
                form={form}
                setForm={setForm}
                product={product}
                categoryOptions={categoryOptions}
                imageUploadTestId="admin-edit-image-upload"
              />
            ) : (
              /* ========== VIEW MODE ========== */
              <div className="space-y-3">
                {/* Header Info */}
                <div>
                  <h3 className="font-semibold text-base" data-testid="product-detail-name">{product.name}</h3>
                  <a
                    href={buildProductPath('tr', product)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 hover:underline mt-1"
                    data-testid="product-detail-link"
                  >
                    <ExternalLink className="w-3 h-3" /> Urunu goruntule
                  </a>
                  <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                    <Badge variant={product.is_active ? "default" : "secondary"}>
                      {product.is_active ? 'Aktif' : 'Pasif'}
                    </Badge>
                    {product.made_in_turkey && <Badge variant="outline" className="text-red-600 border-red-200">Made in Turkiye</Badge>}
                    {product.is_featured && <Badge variant="outline" className="text-amber-600 border-amber-200">One Cikan</Badge>}
                    {product.stock <= 0 && <Badge variant="destructive">Stok Yok</Badge>}
                  </div>
                </div>

                <Separator />

                {/* Price & Stock */}
                <div className="grid grid-cols-2 gap-x-6">
                  <InfoRow label="Fiyat" value={`${product.price?.toFixed(2)} ${product.currency || 'EUR'}`} icon={DollarSign} />
                  {product.compare_price > 0 && <InfoRow label="Eski Fiyat" value={`${product.compare_price?.toFixed(2)}`} icon={DollarSign} />}
                  <InfoRow label="Stok" value={product.stock} icon={Box} />
                  <InfoRow label="SKU" value={product.sku} icon={Tag} />
                  <InfoRow label="Kategori" value={product.category} icon={Package} />
                  <InfoRow label="Marka" value={product.brand} icon={Tag} />
                </div>

                <Separator />

                {/* Details */}
                <div className="grid grid-cols-2 gap-x-6">
                  <InfoRow label="Vergi Orani" value={product.tax_rate ? `%${product.tax_rate}` : null} icon={DollarSign} />
                  <InfoRow label="Kargo yurtici" value={formatShippingTry(product.shipping_cost)} icon={Box} />
                  <InfoRow label="Kargo yurtdisi" value={formatShippingTry(product.international_shipping_cost)} icon={Globe} />
                  <InfoRow label="Agirlik" value={product.weight ? `${product.weight} ${product.weight_unit || 'kg'}` : null} icon={Box} />
                  <InfoRow label="Mensei Ulke" value={product.origin_country} icon={Globe} />
                  <InfoRow label="Puan" value={product.rating > 0 ? `${product.rating} (${product.review_count} yorum)` : 'Henuz yok'} icon={Star} />
                  <InfoRow label="Olusturma" value={product.created_at ? new Date(product.created_at).toLocaleDateString('tr-TR') : null} icon={Calendar} />
                </div>

                {/* Seller Info */}
                {(product.seller_name || product.seller_email) && (
                  <>
                    <Separator />
                    <div className="grid grid-cols-2 gap-x-6">
                      <InfoRow label="Satici" value={product.seller_name} icon={User} />
                      <InfoRow label="Satici Email" value={product.seller_email} icon={User} />
                    </div>
                  </>
                )}

                {/* Description */}
                {product.description && (
                  <>
                    <Separator />
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">Aciklama</p>
                      <p className="text-sm bg-muted/50 rounded-lg p-3 max-h-32 overflow-y-auto whitespace-pre-wrap" data-testid="product-detail-description">
                        {product.description}
                      </p>
                    </div>
                  </>
                )}

                {/* SEO önizleme (Google arama sonucu) */}
                <Separator />
                <div data-testid="product-seo-preview">
                  <div className="flex items-center gap-2 mb-2 flex-wrap">
                    <Search className="w-4 h-4 text-muted-foreground shrink-0" />
                    <p className="text-xs font-medium">SEO önizleme (Google)</p>
                    {product.seo_title?.trim() || product.seo_description?.trim() ? (
                      <Badge variant="outline" className="text-[10px] h-5">Özel alan</Badge>
                    ) : (
                      <Badge variant="secondary" className="text-[10px] h-5">Otomatik</Badge>
                    )}
                  </div>
                  <div className="rounded-lg border bg-white dark:bg-background p-3 space-y-0.5 max-w-xl">
                    <p
                      className="text-[#1a0dab] dark:text-blue-400 text-base leading-snug line-clamp-2"
                      data-testid="product-seo-preview-title"
                    >
                      {resolveProductSeoTitle(product)}
                    </p>
                    <p className="text-[#006621] dark:text-green-500 text-xs truncate" data-testid="product-seo-preview-url">
                      {(() => {
                        const site = getPublicSiteUrl();
                        const path = buildProductPath('tr', product);
                        return site ? `${site}${path}` : path;
                      })()}
                    </p>
                    <p
                      className="text-sm text-[#545454] dark:text-muted-foreground leading-snug line-clamp-3 pt-0.5"
                      data-testid="product-seo-preview-description"
                    >
                      {resolveProductSeoDescription(product, 'tr')}
                    </p>
                  </div>
                  <div className="mt-2 space-y-1 text-[11px] text-muted-foreground">
                    <p>
                      Meta title: {resolveProductSeoTitle(product).length} karakter
                      {product.seo_title?.trim() ? ' · kayıtlı özel değer' : ' · otomatik üretildi'}
                    </p>
                    <p>
                      Meta description: {resolveProductSeoDescription(product, 'tr').length} karakter
                      {product.seo_description?.trim() ? ' · kayıtlı özel değer' : ' · otomatik üretildi'}
                    </p>
                  </div>
                </div>

                {/* Sizes, Colors & Variants */}
                {(product.sizes?.length > 0 || product.colors?.length > 0 || product.variants?.length > 0) && (
                  <>
                    <Separator />
                    {product.variants?.length > 0 ? (
                      <div className="py-1.5">
                        <p className="text-xs text-muted-foreground mb-2">Varyantlar ({product.variants.length})</p>
                        <div className="overflow-x-auto rounded-md border text-xs">
                          <table className="w-full">
                            <thead className="bg-muted/50">
                              <tr>
                                {Object.keys(product.variants[0] || {}).map((k) => (
                                  <th key={k} className="px-2 py-1 text-left font-medium">{k}</th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {product.variants.slice(0, 8).map((row, i) => (
                                <tr key={i} className="border-t">
                                  {Object.keys(product.variants[0] || {}).map((k) => (
                                    <td key={k} className="px-2 py-1">{String(row[k] ?? '-')}</td>
                                  ))}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                          {product.variants.length > 8 && (
                            <p className="text-[11px] text-muted-foreground px-2 py-1">+{product.variants.length - 8} satir daha</p>
                          )}
                        </div>
                      </div>
                    ) : (
                      <div className="grid grid-cols-2 gap-x-6">
                        {product.sizes?.length > 0 && (
                          <div className="py-1.5">
                            <p className="text-xs text-muted-foreground mb-1">Bedenler</p>
                            <div className="flex flex-wrap gap-1">
                              {product.sizes.map((s, i) => <Badge key={i} variant="outline" className="text-xs">{s}</Badge>)}
                            </div>
                          </div>
                        )}
                        {product.colors?.length > 0 && (
                          <div className="py-1.5">
                            <p className="text-xs text-muted-foreground mb-1">Renkler</p>
                            <div className="flex flex-wrap gap-1">
                              {product.colors.map((c, i) => <Badge key={i} variant="outline" className="text-xs">{c}</Badge>)}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </>
                )}

                {/* ID */}
                <Separator />
                <div className="flex items-center gap-2 text-[11px] text-muted-foreground font-mono">
                  <span>ID: {product.id}</span>
                  {product.seller_id && <span>| Satici ID: {product.seller_id.slice(0, 12)}...</span>}
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-12 text-muted-foreground">Urun bulunamadi</div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ProductDetailModal;
